// Case checksum: SHA-256 over the canonical case JSON plus the engine version. Two people who see the same checksum
// entered the same case and ran the same engine. Our own algorithm (the plan calls for "a short hash of the
// canonical inputs"); results are a pure function of inputs + engine, so hashing the inputs + version covers them.

/** Plain JSON value. */
export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

/**
 * Canonical JSON text: object keys sorted (code-point order), no whitespace, `undefined` members dropped, non-finite
 * numbers written as null, -0 as 0. Arrays keep their order.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export function canonicalize(value: unknown): Json {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? (Object.is(value, -0) ? 0 : value) : null;
  if (typeof value === 'boolean' || typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : canonicalize(v)));
  if (typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: { [k: string]: Json } = {};
    for (const k of Object.keys(src).sort()) {
      const v = src[k];
      if (v === undefined || typeof v === 'function') continue;
      out[k] = canonicalize(v);
    }
    return out;
  }
  return null;
}

/** Fields that identify the stored record or hold app data rather than calculation inputs; excluded from the checksum. */
const VOLATILE_KEYS = ['id', 'name', 'appData'];

/** The text that is hashed: canonical case (without record id/name) + "|" + engine version. */
export function checksumPayload(caseData: object, engineVersion: string): string {
  const copy: Record<string, unknown> = { ...(caseData as Record<string, unknown>) };
  for (const k of VOLATILE_KEYS) delete copy[k];
  return `${canonicalJson(copy)}|${engineVersion}`;
}

/** Hex SHA-256 of a string (SubtleCrypto; available in browsers on https/localhost and in Node 20). */
export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** "ABCD-EF01-2345-6789": the first 64 bits of the digest, grouped for reading aloud. */
export function shortChecksum(hex: string): string {
  const h = hex.slice(0, 16).toUpperCase();
  return h.match(/.{4}/g)?.join('-') ?? h;
}

export async function caseChecksum(caseData: object, engineVersion: string): Promise<{ full: string; short: string }> {
  const full = await sha256Hex(checksumPayload(caseData, engineVersion));
  return { full, short: shortChecksum(full) };
}
